import { BookmarkRecord } from 'fl-bookmarks';
import { ErrorCodes } from 'fl-foundation';
import { AdComposedPlayer } from 'fl-player-interface';
import { PlayerResource, ProgramVm, ResourceVm } from 'qp-ui-interface';
import { DeviceTypeModel, getDeviceType } from 'qp-utility';
import { DataService } from '../../data.service';
import { Track } from '../../player/player-audio-subtitles-dialog/player-audio-subtitles-dialog.component';
import { LocalStorageKeys } from '../LocalStorageKeys';
import { CuepointGroup } from '../models/PromoGroup';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import { ScreenService } from '../services/screen/screen.service';
declare let navigator: any;

declare type SubtitlesPreference =
  | 'text_style'
  | 'text_size'
  | 'text_color'
  | 'text_opacity'
  | 'text_edges'
  | 'text_edges_color'
  | 'background_color'
  | 'background_opacity'
  | 'window_color'
  | 'window_opacity';

/**
 * Check if the subtitles are enabled
 * if the subtitles are enabled, check if there are selected some subtitles preference
 * return selected preference if is available and if is different of "off"
 * If there is no selected preference, get the selected app language subtitles
 * if is not avaliable, get the available subtitles
 *
 * @param textTrackList
 * @returns
 */
export const getSelectedSubtitlesTrack = (textTrackList: Track[], localStorage: LocalStorageService): Track => {
  if (localStorage.getItem(LocalStorageKeys.SUBTITLES_ENABLED) && localStorage.getItem(LocalStorageKeys.SUBTITLES_ENABLED) === 'true') {
    if (localStorage.getItem(LocalStorageKeys.SUBTITLES_LANGUAGE) && localStorage.getItem(LocalStorageKeys.SUBTITLES_LANGUAGE) !== 'off') {
      return textTrackList.find((el) => el.language === localStorage.getItem(LocalStorageKeys.SUBTITLES_LANGUAGE));
    } else {
      // get the selected app language
      const lang = textTrackList.find((el) => el.language === localStorage.getItem(LocalStorageKeys.LANG));
      if (lang) {
        return lang;
      } else {
        // return the first available subtitle that is not "off"
        return textTrackList.find((el) => el.language !== 'off');
      }
    }
  }
  return undefined;
};

/**
 * Get saved subtitles preference based on the available values and the local storage key
 * Default: white
 *
 * @param values
 */
const getSavedSubtitlesPreference = (savedStyles: any, styleKey: SubtitlesPreference): string => {
  let value = savedStyles[styleKey];
  if (value) {
    value = value.replace(' ', '-').toLowerCase();
    return `sub-${styleKey}-${value}`;
  }
  return '';
};

/**
 * Returns the saved subtitles preferences based on the available values and the local storage keys
 *
 * @returns
 */
export const getSavedSubtitlesPreferences = (localStorage: LocalStorageService): string[] => {
  if (getDeviceType() === DeviceTypeModel.VIZIO) {
    const classes = [];
    const savedStyles = localStorage.getItem(LocalStorageKeys.SUBTITLES_STYLES)
      ? JSON.parse(localStorage.getItem(LocalStorageKeys.SUBTITLES_STYLES))
      : {};
    classes.push(getSavedSubtitlesPreference(savedStyles, 'text_style'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'text_size'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'text_color'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'text_opacity'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'background_color'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'background_opacity'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'text_edges'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'text_edges_color'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'window_color'));
    classes.push(getSavedSubtitlesPreference(savedStyles, 'window_opacity'));
    return classes.filter((el) => !!el);
  }
  return [];
};

/**
 * Check if is music content type
 * TODO: confirm audio content types
 */
export const isAudioContent = (metadataType: string): boolean => {
  return metadataType === 'music';
};

export const isTrailer = (metadataType: string): boolean => {
  return metadataType === 'trailer';
};

export const isTvEpisode = (metadataType: string): boolean => {
  return metadataType === 'tvepisode';
};

/**
 * Check if is live, livechannel o channel content type
 */
export const isLiveContent = (currentResource: ResourceVm): boolean => {
  return currentResource.type === 'liveevent' || currentResource.type === 'livechannel' || currentResource.type === 'channel';
};

/**
 * Check if is live, livechannel o channel metadata type
 */
export const isLiveByMetadata = (metadataType: string, includeEvent: boolean = true): boolean => {
  if (!includeEvent) {
    return metadataType === 'livechannel' || metadataType === 'channel';
  }
  return metadataType === 'liveevent' || metadataType === 'livechannel' || metadataType === 'channel';
};

export const validateToPlay = (contentType: string) => {
  return ['livechannel', 'channel', 'shortvideo'].includes(contentType);
};

/**
 * Check if the bookmark belongs to the resource since
 * the bookmarks are now at series level
 * and must check if the resource matches the episode.
 *
 * @param currentResource
 * @param response
 * @returns
 */
export const isResourceBookmark = (contentType: string, contentId: string, response: BookmarkRecord): boolean => {
  return response && (!contentType.includes('episode') || (contentType.includes('episode') && contentId === response.episodeId));
};

/**
 * Check if the connection is getting slow acording to navigator info
 *
 * @returns
 */
export const isSlowConnection = (): boolean => {
  return (
    navigator.connection?.downlink <= 0.6 ||
    navigator.connection?.effectiveType === '2g' ||
    navigator.connection?.effectiveType === 'slow-2g'
  );
};

/**
 * Validate if the resource should show up next
 *
 * @param currentResource
 * @returns
 */
export const haveUpNext = (currentResource: ResourceVm, localStorage: LocalStorageService): boolean => {
  return (
    localStorage.getItem(LocalStorageKeys.AUTOPLAY_PREFERENCE) === 'true' &&
    currentResource.contentType !== 'trailer' &&
    currentResource.contentType !== 'shortvideo' &&
    !isLiveContent(currentResource)
  );
};

export const showRecommendations = (currentResource: ResourceVm, metadataType: string): boolean => {
  return !(
    isLiveContent(currentResource) ||
    isLiveByMetadata(metadataType) ||
    isAudioContent(metadataType) ||
    isTrailer(metadataType) ||
    isTvEpisode(metadataType)
  );
};

/**
 * Get player resource from resourceVm
 *
 * @param currentResource
 * @returns
 */
export const getPlayerResource = (currentResource: ResourceVm, position: number): PlayerResource => {
  if (!currentResource) return undefined;
  if (currentResource.isLive) {
    return {
      image: currentResource.image,
      title: currentResource.title,
      uniqueId: currentResource.key,
      metadataType: currentResource.type,
      enableToPlay: currentResource.contentType?.toLowerCase() === 'channel',
      position,
    };
  }
  const trailer = currentResource.trailers && currentResource.trailers.length > 0 ? currentResource.trailers[0] : null;
  return {
    image: currentResource.image,
    title: currentResource.title,
    uniqueId: trailer ? trailer : currentResource.key,
    metadataType: trailer ? 'trailer' : currentResource.type,
    enableToPlay: currentResource.trailers && currentResource.trailers.length > 0,
    position,
  };
};

/**
 * Get player resource from programVm
 *
 * @param program
 * @returns
 */
export const getPlayerProgramResource = (program: ProgramVm): PlayerResource => {
  if (!program) return undefined;
  return {
    image: program.image,
    title: program.name,
    uniqueId: program.channelId,
    metadataType: 'channel',
    enableToPlay: true,
    position: 0,
  };
};

/**
 * Check Error belongs to player or server
 * return errorCode and errorMessage
 *
 * @param error
 * @returns
 */
export const getPlayerErrorInfo = (error: any): { errorCode: number | string; errorMessage: string } => {
  const isPlayerError = error && error.doesBelongToCategory && error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK);
  const isServerError = error && error.doesBelongToCategory && error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_SERVER);
  const is3rdPartyError = error && error.doesBelongToCategory && error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY);
  let errorCode = error?.errorCode;
  let errorMessage = error?.errorMessage;
  if (isPlayerError) {
    errorCode = error?.errorCode;
    errorMessage = `${error?.errorMessage} Code: ${error?.internalError?.errorCode}`;
  } else if (is3rdPartyError) {
    errorCode = error?.errorCode;
    errorMessage = error?.errorMessage;
  } else if (isServerError) {
    errorCode = isServerError ? error?.internalError?.errorCode : error?.errorCode;
    errorMessage = isServerError ? error?.internalError?.errorMessage : error?.errorMessage;
  } else {
    if (error.message) errorMessage = error.message;
    errorCode = ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK;
  }
  return { errorCode, errorMessage };
};
/**
 * Check the player status and return the back url that should be navigated
 *
 * @param dataService
 * @param screenService
 * @param currentResource
 * @param isLiveContent
 * @returns
 */
export const getPlayerBackUrl = (
  dataService: DataService,
  screenService: ScreenService,
  currentResource: ResourceVm,
  isLiveContent: boolean,
  seriesResource?: ResourceVm,
  isCatchup?: boolean
): string => {
  try {
    if (dataService.isContinueWatching) {
      return `${screenService.searchlink}`;
    } else if (currentResource?.contentType?.includes('trailer') || currentResource?.type?.includes('tvbonus')) {
      return 'LOCATION_BACK';
    } else if (
      ((isLiveContent || isCatchup) && currentResource.contentType?.toLowerCase() !== 'liveevent') ||
      dataService.activeTab === 'epg'
    ) {
      return `${dataService.activeTab}`;
    } else if (seriesResource) {
      return seriesResource ? `${seriesResource.contentType}/${seriesResource.uniqueId}` : `tvseries/${currentResource.seriesId}`;
    } else {
      return `${currentResource.type}/${currentResource.id}`;
    }
  } catch (error) {
    return dataService.homeTab;
  }
};

/**
 * Get the cuepoints groups from the player
 *
 * @param promos
 * @returns
 */
export const getCuepointGroups = (player: AdComposedPlayer): CuepointGroup[] => {
  const points = player.getCuePoints();
  if (!points) return [];
  return points.map((point) => ({
    startTime: point,
  }));
};
